import React, { useEffect, useRef, useState } from 'react';

// Records a short voice note in the browser and hands the audio Blob to onChange (null when cleared).
// existingUrl shows an already-saved note (edit mode); onRemoveExisting lets the user drop it.
const MAX_SECONDS = 120;

const pickMimeType = () => {
  if (typeof MediaRecorder === 'undefined') return '';
  return ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg'].find((t) => MediaRecorder.isTypeSupported(t)) || '';
};

const formatTime = (secs) => `${Math.floor(secs / 60)}:${String(secs % 60).padStart(2, '0')}`;

const VoiceRecorder = ({ onChange, existingUrl, onRemoveExisting }) => {
  const [status, setStatus] = useState('idle'); // idle | recording | recorded
  const [seconds, setSeconds] = useState(0);
  const [previewUrl, setPreviewUrl] = useState('');
  const [error, setError] = useState('');
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const chunksRef = useRef([]);

  const stopStream = () => {
    clearInterval(timerRef.current);
    if (streamRef.current) streamRef.current.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  };

  // Release the microphone and preview URL when the form goes away
  useEffect(() => () => {
    stopStream();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const startRecording = async () => {
    setError('');
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
      setError('Voice recording is not supported in this browser.');
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stopStream();
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        setPreviewUrl(URL.createObjectURL(blob));
        setStatus('recorded');
        onChange(blob);
      };
      recorderRef.current = recorder;
      recorder.start();
      setSeconds(0);
      setStatus('recording');
      timerRef.current = setInterval(() => {
        setSeconds((prev) => {
          if (prev + 1 >= MAX_SECONDS && recorder.state === 'recording') recorder.stop();
          return prev + 1;
        });
      }, 1000);
    } catch {
      stopStream();
      setError('Microphone access was blocked. Please allow the microphone and try again.');
    }
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state === 'recording') recorderRef.current.stop();
  };

  const discard = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl('');
    setSeconds(0);
    setStatus('idle');
    onChange(null);
  };

  return (
    <div className="mt-1.5 rounded-xl border border-yellow-200 bg-yellow-50/60 px-4 py-3">
      {status === 'idle' && existingUrl && (
        <div className="mb-3 flex flex-wrap items-center gap-3">
          <audio controls src={existingUrl} className="h-9 max-w-full" />
          {onRemoveExisting && (
            <button type="button" onClick={onRemoveExisting} className="text-xs font-bold text-red-600 hover:underline">Remove saved note</button>
          )}
        </div>
      )}

      {status === 'idle' && (
        <button
          type="button"
          onClick={startRecording}
          className="inline-flex items-center gap-2 rounded-xl bg-black px-4 py-2 text-sm font-black text-yellow-400 hover:bg-gray-900"
        >
          🎤 {existingUrl ? 'Record a new voice note' : 'Start recording'}
        </button>
      )}

      {status === 'recording' && (
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-2 text-sm font-black text-red-600">
            <span className="h-3 w-3 animate-pulse rounded-full bg-red-600" />
            Recording {formatTime(seconds)} / {formatTime(MAX_SECONDS)}
          </span>
          <button type="button" onClick={stopRecording} className="rounded-xl bg-red-600 px-4 py-2 text-sm font-black text-white hover:bg-red-700">
            ■ Stop
          </button>
        </div>
      )}

      {status === 'recorded' && (
        <div className="flex flex-wrap items-center gap-3">
          <audio controls src={previewUrl} className="h-9 max-w-full" />
          <span className="text-xs font-bold text-emerald-700">✓ {formatTime(seconds)} recorded</span>
          <button type="button" onClick={discard} className="text-xs font-bold text-red-600 hover:underline">Delete & re-record</button>
        </div>
      )}

      <p className="mt-2 text-xs text-gray-500">Optional · up to 2 minutes. Speak anything you want us to know about this member.</p>
      {error && <p className="mt-1 text-xs font-bold text-red-600">⚠ {error}</p>}
    </div>
  );
};

export default VoiceRecorder;
